import { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import DraftsTable from './DraftsTable';
import { listDrafts } from '../../../api/paymentsApi/settlementsApi';
import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import TableContainer from '../../../components/Table/TableContainer';
import usePagination from '../../../hooks/usePagination';
import { MemoDraftSummary, ActiveSortField, FilterTypes } from '../../../types';
import useSnackBar from '../../../hooks/useSnackBar';
import TableActions from '../../../components/Table/TableActions';
import TableFilters from '../../../components/TableFilters/TableFilters';

// Table column labels for drafts list
const getDraftsListTableLabels = () => [
  {
    accessor: '',
    isSortable: false,
    label: 'Case Reference ID',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Case Name',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Client',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Defendant',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Settled Amount',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Filename',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Updated',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Created',
  },
];

const DraftsTab = () => {
  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();

  const [draftsList, setDraftsList] = useState<MemoDraftSummary[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [filters, setFilters] = useState<FilterTypes>({
    clientId: '',
    defendantId: '',
  });

  // Fetch drafts with server-side filtering
  useEffect(() => {
    const filtersWithoutFirmId = { ...filters } as FilterTypes & { firmId?: string };
    delete filtersWithoutFirmId.firmId;

    setIsDataLoading(true);
    listDrafts(pageNumber, rowsPerPage, filtersWithoutFirmId)
      .then((res) => {
        if (res.data) {
          setDraftsList(res.data.data);
          pageNumber === 1 && setTotalCount?.(res.data.total || 0);
        }
      })
      .catch((error) => {
        setAlert({
          message: error.response?.data?.message || 'Failed to load drafts',
          type: 'error',
        });
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [pageNumber, rowsPerPage, filters, setAlert, setTotalCount]);

  const tableLabels = getDraftsListTableLabels();

  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);

  // const exportList = () => {
  //   // For now, just show a message since backend doesn't support export yet
  //   setAlert({
  //     message: 'Draft export functionality will be available soon.',
  //     type: 'info',
  //   });
  // };

  return (
    <>
      <TableActions
        hasParams={false}
        // exportList={exportList}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />

      {showFilters && (
        <TableFilters
          filters={filters}
          setFilters={setFilters}
          client
          defendant
          timeFilter="Updated Date"
        />
      )}

      {isDataLoading && !draftsList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : !draftsList.length ? (
        <NoDataText />
      ) : (
        <TableContainer
          headerLabels={tableLabels}
          activeSortField={activeSortField}
          setActiveSortField={setActiveSortField}
        >
          <DraftsTable tableData={draftsList} showCaseInfo={true} />
        </TableContainer>
      )}
    </>
  );
};

export default DraftsTab;
